/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { Initializer } from '@adobe-commerce/elsie/lib';
import { Lang } from '@adobe-commerce/elsie/i18n';
import { events } from '@adobe-commerce/event-bus';
import {
  state,
  DEFAULT_CONFIG,
  DEFAULT_PERMISSIONS,
} from '@/quote-management/lib/state';
import {
  getQuoteData,
  getStoreConfig,
  getQuoteTemplateData,
} from '@/quote-management/api';
import { mapAuthPermissions, AuthPermissionsPayload } from '@/quote-management/utils/mapAuthPermissions';

type ConfigProps = {
  langDefinitions?: Lang;
  quoteId?: string;
  quoteTemplateId?: string;
};

function loadQuoteData(permissions: typeof DEFAULT_PERMISSIONS) {
  // Prevent loading quote data twice
  if (state.quoteDataLoaded) return;

  const config = initialize.config.getConfig();
  const { quoteId, quoteTemplateId } = config;


  // Only proceed if we have permissions and at least one ID to load
  if (!permissions.editQuote || (!quoteId && !quoteTemplateId)) {
    return;
  }

  // Mark as loaded before starting the async operations to prevent duplicate calls
  state.quoteDataLoaded = true;

  if (quoteId) {
    getQuoteData(quoteId)
      .then((quote) => {
        if (!state.quoteDataInitialized) {
          events.emit('quote-management/quote-data/initialized', { quote, permissions });
        }
        state.quoteDataInitialized = true;
      })
      .catch((error) => {
        state.quoteDataLoaded = false;
        events.emit('quote-management/quote-data/error', { error });
      });
  }

  if (quoteTemplateId) {
    getQuoteTemplateData(quoteTemplateId)
      .catch((error) => {
        state.quoteDataLoaded = false;
        events.emit('quote-management/quote-template-data/error', { error });
      });
  }
}

export const initialize = new Initializer<ConfigProps>({
  init: async (config) => {
    const defaultConfig = {};

    // Set config
    initialize.config.setConfig({ ...defaultConfig, ...config });

    // Fetch store config
    await getStoreConfig()
      .then((config) => {
        state.config = config;
      })
      .catch((error) => {
        console.error('Failed to fetch store config: ', error);
        state.config = DEFAULT_CONFIG;
      });

    // Mark initialization as complete
    state.initialized = true;
    events.emit('quote-management/initialized', { config: state.config });
  },

  listeners: () => [
    events.on('authenticated', async (authenticated) => {
      state.authenticated = !!authenticated;

      // Defensive: reset permissions if user logs out
      // This ensures permissions are cleared even if auth/permissions event doesn't fire
      if (!authenticated) {
        state.permissions = DEFAULT_PERMISSIONS;
        state.quoteDataLoaded = false;
        events.emit('quote-management/permissions', DEFAULT_PERMISSIONS);
      }
    },
      {
        eager: true,
      }
    ),

    events.on('auth/permissions', async (payload: AuthPermissionsPayload) => {
      // Map the auth permissions payload to internal permissions structure
      const permissions = mapAuthPermissions(payload);

      state.permissions = permissions;
      // Reset quote data loaded flag when permissions change to allow reloading
      state.quoteDataLoaded = false;
      events.emit('quote-management/permissions', state.permissions);
    }, {
      eager: true,
    }),

    events.on('quote-management/permissions', async (permissions) => {
      // Only process quote data loading after initialization is complete
      if (!state.initialized) {
        return;
      }

      loadQuoteData(permissions);
    }, { eager: true }),

    events.on('quote-management/initialized', async () => {
      loadQuoteData(state.permissions);
    }, { eager: true }),

    events.on('checkout/updated', async (data) => {
      // Only process after initialization is complete
      if (!state.initialized) {
        return;
      }

      if (data?.type !== 'quote') {
        return;
      }

      // Reset the flag to allow reloading of quote data
      state.quoteDataLoaded = false;
      loadQuoteData(state.permissions);
    }, { eager: true }),
  ],
});

export const config = initialize.config;
